// ABOUTME: Boots the NovaName public shell and authenticated naming workspace.
// ABOUTME: Session and provider readiness are loaded from the same-origin Worker.
import {
  lazy,
  StrictMode,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { api, message } from "./api";
import type { PublicConfig, Session } from "./api";
import { Landing } from "./components/Landing";
import { Login } from "./components/PublicPages";
import { ConsentPage, ContinueAuthorization } from "./components/ConsentPage";
import { BrandLogo } from "./components/BrandLogo";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { Button, Notice } from "./components/ui/controls";
import "./styles/global.css";
const resetToken =
  location.pathname === "/reset-password"
    ? (new URLSearchParams(location.search).get("token") ?? "")
    : "";
if (location.pathname === "/reset-password" && location.search)
  history.replaceState(history.state, "", "/reset-password");
const AdminWorkspace = lazy(() =>
  import("./components/AdminWorkspace").then((module) => ({
    default: module.AdminWorkspace,
  })),
);
// The workspace and shared shortlists load on their own, so the landing page stays small.
const Workspace = lazy(() =>
  import("./components/Workspace").then((module) => ({
    default: module.Workspace,
  })),
);
const SharedShortlistPage = lazy(() =>
  import("./components/SharedShortlist").then((module) => ({
    default: module.SharedShortlistPage,
  })),
);
const ResearchGuide = lazy(() =>
  import("./components/ResearchGuide").then((module) => ({
    default: module.ResearchGuide,
  })),
);

const workspaceLoading = (
  <main className="app-loading">
    <BrandLogo />
    <p role="status">Opening your private workspace…</p>
  </main>
);

function App() {
  const [path, setPath] = useState(location.pathname);
  const adminPath = path === "/admin" || path.startsWith("/admin/");
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const loadConfig = useCallback(() => {
    setConfigError("");
    void api
      .config()
      .then(setConfig)
      .catch((error) => setConfigError(message(error)));
  }, []);
  const loadSession = useCallback(() => {
    setSessionLoading(true);
    setSessionError("");
    void api
      .session()
      .then(setSession)
      .catch((error) => setSessionError(message(error)))
      .finally(() => setSessionLoading(false));
  }, []);
  useEffect(() => {
    loadConfig();
    loadSession();
    const pop = () => setPath(location.pathname);
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [loadConfig, loadSession]);
  function navigate(next: string) {
    history.pushState({}, "", next);
    setPath(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  }
  useEffect(() => {
    if (
      path === "/login" &&
      session &&
      !sessionLoading &&
      !new URLSearchParams(location.search).has("client_id")
    ) {
      history.replaceState({}, "", "/app");
      setPath("/app");
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [path, session, sessionLoading]);
  useEffect(() => {
    document.title = adminPath
      ? "Administration · NovaName"
      : path.startsWith("/app")
        ? "Your naming workspace · NovaName"
        : path === "/guide"
          ? "Research guide · NovaName"
          : path === "/consent"
            ? "Connect your account · NovaName"
            : path === "/login"
              ? "Sign in · NovaName"
              : path === "/reset-password"
                ? "Reset your password · NovaName"
                : path.startsWith("/s/")
                  ? "Shared shortlist · NovaName"
                  : "NovaName — Find your next name";
  }, [path, adminPath]);
  if (path.startsWith("/s/"))
    return (
      <Suspense
        fallback={
          <main className="app-loading">
            <p role="status">Opening the shortlist…</p>
          </main>
        }
      >
        <SharedShortlistPage token={path.slice(3)} />
      </Suspense>
    );
  if (path === "/guide")
    return (
      <Suspense
        fallback={
          <main className="app-loading">
            <p role="status">Opening the research guide…</p>
          </main>
        }
      >
        <ResearchGuide />
      </Suspense>
    );
  if (path === "/reset-password")
    return (
      <ResetPasswordPage
        token={resetToken}
        config={config}
        navigate={navigate}
        onSessionChange={loadSession}
      />
    );
  if (path.startsWith("/app") || adminPath || path === "/consent") {
    if (sessionLoading) return workspaceLoading;
    if (sessionError)
      return (
        <main className="consent-layout">
          <BrandLogo />
          <Notice
            kind="error"
            action={
              <Button variant="secondary" onClick={loadSession}>
                Retry
              </Button>
            }
          >
            {sessionError}
          </Notice>
        </main>
      );
    if (session && path === "/consent")
      return <ConsentPage session={session} />;
    if (session && adminPath)
      return (
        <Suspense
          fallback={
            <main className="app-loading">
              <p role="status">Opening administration…</p>
            </main>
          }
        >
          <AdminWorkspace session={session} navigate={navigate} />
        </Suspense>
      );
    if (session)
      return (
        <Suspense fallback={workspaceLoading}>
          <Workspace
            session={session}
            config={config}
            navigate={navigate}
            onSessionChange={loadSession}
          />
        </Suspense>
      );
  }
  if (
    path === "/login" &&
    session &&
    !sessionLoading &&
    new URLSearchParams(location.search).has("client_id")
  )
    return <ContinueAuthorization />;
  if (path === "/login" && session && !sessionLoading)
    return (
      <Suspense fallback={workspaceLoading}>
        <Workspace
          session={session}
          config={config}
          navigate={navigate}
          onSessionChange={loadSession}
        />
      </Suspense>
    );
  if (
    path === "/login" ||
    path.startsWith("/app") ||
    adminPath ||
    path === "/consent"
  )
    return (
      <Login
        navigate={navigate}
        config={config}
        configError={configError}
        refreshConfig={loadConfig}
        onSessionChange={loadSession}
      />
    );
  return (
    <>
      <a className="skip-link" href="#hero-title">
        Skip to content
      </a>
      <Landing navigate={navigate} config={config} />
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
